import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Paper
} from '@mui/material';
import {
  ExpandMore,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Lightbulb,
  Assignment,
  Person,
  Psychology
} from '@mui/icons-material';

interface ValidationResult {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  suggestions: string[];
}

interface OnboardingTask {
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  due_days: number;
  category: string;
}

interface AIEmployeeAssistantProps {
  employeeData?: any;
  onEmployeeNumberSuggestion?: (number: string) => void;
  onValidationComplete?: (result: ValidationResult) => void;
}

const AIEmployeeAssistant: React.FC<AIEmployeeAssistantProps> = ({
  employeeData,
  onEmployeeNumberSuggestion,
  onValidationComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [onboardingTasks, setOnboardingTasks] = useState<OnboardingTask[]>([]);
  const [suggestedNumber, setSuggestedNumber] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [demoEmployeeData, setDemoEmployeeData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    social_security_number: '',
    postcode: '',
    job_title: '',
    department: '',
    annual_salary: '',
    date_of_birth: '',
    work_location: ''
  });

  const validateEmployeeData = async () => {
    // Use demo data if no employee data is passed
    const dataToValidate = employeeData || demoEmployeeData;

    // Check if we have any data to validate
    const hasData = Object.values(dataToValidate).some(value => value && value.toString().trim() !== '');
    if (!hasData) {
      alert('Please enter some employee data to validate');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-assistant/validate-employee', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employee_data: dataToValidate
        }),
      });

      if (!response.ok) {
        throw new Error('Validation failed');
      }

      const data = await response.json();
      setValidationResult(data.validation);

      if (onValidationComplete) {
        onValidationComplete(data.validation);
      }
    } catch (error) {
      console.error('Validation error:', error);
      alert('Failed to validate employee data');
    } finally {
      setLoading(false);
    }
  };

  const suggestEmployeeNumber = async () => {
    if (!department) {
      alert('Please enter a department');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-assistant/suggest-employee-number', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          department: department
        }),
      });

      if (!response.ok) {
        throw new Error('Employee number suggestion failed');
      }

      const data = await response.json();
      setSuggestedNumber(data.suggested_employee_number);

      if (onEmployeeNumberSuggestion) {
        onEmployeeNumberSuggestion(data.suggested_employee_number);
      }
    } catch (error) {
      console.error('Employee number suggestion error:', error);
      alert('Failed to suggest employee number');
    } finally {
      setLoading(false);
    }
  };

  const generateOnboardingTasks = async () => {
    // Use demo data if no employee data is passed
    const dataToGenerate = employeeData || demoEmployeeData;

    // Check if we have minimum required data
    if (!dataToGenerate.department && !dataToGenerate.job_title) {
      alert('Please enter at least a department and job title to generate onboarding tasks');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/ai-assistant/generate-onboarding-tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employee_data: dataToGenerate
        }),
      });

      if (!response.ok) {
        throw new Error('Onboarding tasks generation failed');
      }

      const data = await response.json();
      setOnboardingTasks(data.onboarding_tasks);
    } catch (error) {
      console.error('Onboarding tasks error:', error);
      alert('Failed to generate onboarding tasks');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Psychology sx={{ mr: 1 }} />
        AI Employee Assistant
      </Typography>

      <Grid container spacing={3}>
        {/* Employee Number Suggestion */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                Smart Employee Number
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Department"
                  value={department}
                  onChange={(e) => setDepartment(e.target.value)}
                  size="small"
                  placeholder="e.g., Sales, IT, Finance"
                />
                <Button
                  variant="contained"
                  onClick={suggestEmployeeNumber}
                  disabled={loading || !department}
                >
                  {loading ? <CircularProgress size={20} /> : 'Suggest'}
                </Button>
              </Box>
              {suggestedNumber && (
                <Alert severity="success">
                  Suggested Employee Number: <strong>{suggestedNumber}</strong>
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Data Validation */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
                Data Validation
              </Typography>
              <Button
                variant="contained"
                onClick={validateEmployeeData}
                disabled={loading || !employeeData}
                fullWidth
              >
                {loading ? <CircularProgress size={20} /> : 'Validate Employee Data'}
              </Button>

              {validationResult && (
                <Box sx={{ mt: 2 }}>
                  {validationResult.errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">Errors:</Typography>
                      <List dense>
                        {validationResult.errors.map((error, index) => (
                          <ListItem key={index}>
                            <ListItemIcon><ErrorIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={error} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}

                  {validationResult.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2">Warnings:</Typography>
                      <List dense>
                        {validationResult.warnings.map((warning, index) => (
                          <ListItem key={index}>
                            <ListItemIcon><Warning fontSize="small" /></ListItemIcon>
                            <ListItemText primary={warning} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}

                  {validationResult.suggestions.length > 0 && (
                    <Alert severity="info">
                      <Typography variant="subtitle2">Suggestions:</Typography>
                      <List dense>
                        {validationResult.suggestions.map((suggestion, index) => (
                          <ListItem key={index}>
                            <ListItemIcon><Lightbulb fontSize="small" /></ListItemIcon>
                            <ListItemText primary={suggestion} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Onboarding Tasks */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <Assignment sx={{ mr: 1, verticalAlign: 'middle' }} />
                AI Onboarding Tasks
              </Typography>
              <Button
                variant="contained"
                onClick={generateOnboardingTasks}
                disabled={loading || !employeeData}
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={20} /> : 'Generate Onboarding Tasks'}
              </Button>

              {onboardingTasks.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Generated {onboardingTasks.length} personalized tasks:
                  </Typography>

                  {onboardingTasks.map((task, index) => (
                    <Accordion key={index}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Typography sx={{ flexGrow: 1 }}>{task.title}</Typography>
                          <Chip
                            label={task.priority}
                            color={getPriorityColor(task.priority) as any}
                            size="small"
                            sx={{ mr: 2 }}
                          />
                          <Chip
                            label={`Day ${task.due_days}`}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Category: {task.category}
                        </Typography>
                        <Typography variant="body1">
                          {task.description}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Demo Employee Data Form */}
        {!employeeData && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  🧪 Test AI Features with Demo Data
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Enter employee information below to test AI validation and onboarding task generation:
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={demoEmployeeData.first_name}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, first_name: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={demoEmployeeData.last_name}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, last_name: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={demoEmployeeData.email}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, email: e.target.value }))}
                      size="small"
                      placeholder="john.smith@company.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Social Security Number"
                      value={demoEmployeeData.social_security_number}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, social_security_number: e.target.value }))}
                      size="small"
                      placeholder="AB123456C"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Postcode"
                      value={demoEmployeeData.postcode}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, postcode: e.target.value }))}
                      size="small"
                      placeholder="SW1A 1AA"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Department"
                      value={demoEmployeeData.department}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, department: e.target.value }))}
                      size="small"
                      placeholder="Sales, IT, Finance, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={demoEmployeeData.job_title}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, job_title: e.target.value }))}
                      size="small"
                      placeholder="Software Developer, Sales Manager, etc."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Annual Salary"
                      value={demoEmployeeData.annual_salary}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, annual_salary: e.target.value }))}
                      size="small"
                      placeholder="45000"
                      type="number"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Work Location"
                      value={demoEmployeeData.work_location}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, work_location: e.target.value }))}
                      size="small"
                      placeholder="office, remote, hybrid"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      value={demoEmployeeData.date_of_birth}
                      onChange={(e) => setDemoEmployeeData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      size="small"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setDemoEmployeeData({
                      first_name: 'John',
                      last_name: 'Smith',
                      email: 'john.smith@techflowsolutions.co.uk',
                      social_security_number: 'AB123456C',
                      postcode: 'SW1A 1AA',
                      job_title: 'Software Developer',
                      department: 'IT',
                      annual_salary: '55000',
                      date_of_birth: '1990-05-15',
                      work_location: 'remote'
                    })}
                  >
                    Fill Sample Data
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setDemoEmployeeData({
                      first_name: '',
                      last_name: '',
                      email: '',
                      social_security_number: '',
                      postcode: '',
                      job_title: '',
                      department: '',
                      annual_salary: '',
                      date_of_birth: '',
                      work_location: ''
                    })}
                  >
                    Clear All
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AIEmployeeAssistant;
